import React, { useMemo, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { condition_statuses } from "../enums";
import useSubsectionCrud from "./useSubsectionCrud";

const purple = "#2E2868";


const MedicalHistorySubsection = ({
  subjectId,
  isCreate,
  form,
  medicalHistory,
  setMedicalHistory,
  isOpen,
  onToggle,
  saveSubject,
  loadSubject,
  getConditions,
}) => {
  const { saveOne, deleteOne } = useSubsectionCrud({ subjectId, saveSubject, loadSubject });

  const [showAddMedicalHistory, setShowAddMedicalHistory] = useState(false);
  const [editingMedicalIndex, setEditingMedicalIndex] = useState(null);

  const [conditions, setConditions] = useState([]);
  const [isCustomSelected, setIsCustomSelected] = useState(false);

  const [newMedicalHistory, setNewMedicalHistory] = useState({
    id: null,
    condition_id: null, 
    condition_name: "",
    condition_status: "",
    notes: "",
  });

  // --- Load Conditions
  useEffect(() => {
    let mounted = true;
    (async () => {
      const res = await getConditions();
      if (!mounted) return;
      setConditions(res || []);
    })();
    return () => {
      mounted = false;
    };
  }, [getConditions]);

  const handleMedicalHistoryChange = (field, val) =>
    setNewMedicalHistory((prev) => ({ ...prev, [field]: val }));

  const resetForm = () => {
    setNewMedicalHistory({
      id: null,
      condition_id: null,
      condition_name: "",
      condition_status: "",
      notes: "",
    });
    setEditingMedicalIndex(null);
    setShowAddMedicalHistory(false);
    setIsCustomSelected(false);
  };

  
  const findConditionById = (rawId) => {
    if (rawId === "" || rawId == null) return null;
    if (rawId === "__CUSTOM__") return { id: null, name: "Custom Condition" };
    return (conditions || []).find((c) => String(c?.id) === String(rawId)) || null;
  };

 
  const conditionOptions = useMemo(() => {
    return (conditions || []).map((c) => {
      const isCustom = c?.id == null;
      return {
        value: isCustom ? "__CUSTOM__" : String(c.id),
        label: c?.name ?? String(c?.id ?? ""),
        isCustom,
        raw: c,
      };
    });
  }, [conditions]);

  const selectedConditionValue = useMemo(() => {
    if (isCustomSelected) return "__CUSTOM__";
    return newMedicalHistory.condition_id == null ? "" : String(newMedicalHistory.condition_id);
  }, [isCustomSelected, newMedicalHistory.condition_id]);

  const syncFromFreshSubject = (fresh) => {
    if (!fresh?.subject_medical_history) return;

    setMedicalHistory(
      fresh.subject_medical_history.map((m) => ({
        id: m.id,
        condition_id: m.condition_id ?? null,
        condition_name:
          m.condition_name ||
          m.condition_id_name ||
          (m.condition && m.condition.name) ||
          "",
        condition_status: m.condition_status ?? "",
        notes: m.notes ?? "",
      }))
    );
  };

  const saveMedicalHistory = async () => {
    // Validation
    const hasCondition =
      newMedicalHistory.condition_id != null || !!newMedicalHistory.condition_name?.trim();
    if (!hasCondition || !newMedicalHistory.condition_status) return;

    // Payload (whitelist)
    const payloadObject = {
      ...(newMedicalHistory.id && { id: newMedicalHistory.id }),
      condition_id: newMedicalHistory.condition_id ?? null,
      condition_name:
        newMedicalHistory.condition_id != null ? null : newMedicalHistory.condition_name.trim(),
      condition_status: (newMedicalHistory.condition_status || "").trim(),
      notes: (newMedicalHistory.notes || "").trim() || null,
    };

    const updatedList =
      editingMedicalIndex !== null
        ? medicalHistory.map((item, idx) =>
            idx === editingMedicalIndex ? { ...item, ...payloadObject } : item
          )
        : [...medicalHistory, payloadObject];

    
    if (isCreate && form?.setValue) {
      setMedicalHistory(updatedList);
      form.setValue({ key: "subject_medical_history", value: updatedList });
      resetForm();
      return;
    }

    // Edit mode -> backend (shared pattern)
    await saveOne({
      key: "subject_medical_history",
      payloadObject,
      syncFromFreshSubject,
    });

    resetForm();
  };

  const deleteCurrentMedicalHistory = async () => {
    if (isCreate || editingMedicalIndex === null) return;

    const record = medicalHistory[editingMedicalIndex];
    if (!record?.id) return;

    await deleteOne({
      key: "subject_medical_history",
      id: record.id,
      syncFromFreshSubject,
    });

    resetForm();
  };

  return (
    <div className="mb-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center" onClick={onToggle} style={{ cursor: "pointer" }}>
          <i
            className={`bi ${isOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
            style={{ color: purple, fontSize: "14px", marginRight: "6px" }}
          />
          <h6 className="fw-bold m-0" style={{ color: purple }}>
            Medical History ({medicalHistory.length})
          </h6>
        </div>

        {/* ADD Button */}
        <span
          onClick={() => {
            setShowAddMedicalHistory(true);
            setEditingMedicalIndex(null);
            setNewMedicalHistory({
              id: null,
              condition_id: null,
              condition_name: "",
              condition_status: "",
              notes: "",
            });
            setIsCustomSelected(false);
          }}
          style={{ fontSize: "13px", color: purple, cursor: "pointer", fontWeight: 600 }}
        >
          + Add
        </span>
      </div>

      {/* FORM */}
      {showAddMedicalHistory && (
        <div className="p-3 mb-3 position-relative" style={{ background: "#f2f2f7", borderRadius: "12px" }}>
          <span
            onClick={() => setShowAddMedicalHistory(false)}
            style={{
              position: "absolute",
              right: "12px",
              top: "10px",
              cursor: "pointer",
              color: purple,
              fontWeight: "bold",
              fontSize: "23px",
            }}
          >
            ×
          </span>

          {/* Condition */}
          <div className="mb-2">
            <label className="mb-1">Condition</label>
            <select
              value={selectedConditionValue}
              onChange={(e) => {
                const val = e.target.value;

                if (val === "__CUSTOM__") {
                  setIsCustomSelected(true);
                  setNewMedicalHistory((prev) => ({
                    ...prev,
                    condition_id: null,
                    condition_name: "",
                  }));
                  return;
                }

                const selected = findConditionById(val);
                setIsCustomSelected(false);
                setNewMedicalHistory((prev) => ({
                  ...prev,
                  condition_id: selected?.id ?? null,
                  condition_name: selected?.name ?? "",
                }));
              }}
              className="form-control rounded-pill border-0"
            >
              <option value="">Select condition</option>
              {conditionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Condition Name Input */}
          {isCustomSelected && (
            <div className="mb-2">
              <label className="mb-1">Custom Condition Name</label>
              <input
                value={newMedicalHistory.condition_name}
                onChange={(e) => handleMedicalHistoryChange("condition_name", e.target.value)}
                className="form-control rounded-pill border-0"
                placeholder="Enter condition name"
              />
            </div>
          )}

          {/* Status */}
          <div className="mb-2">
            <label className="mb-1">Status</label>
            <select
              value={newMedicalHistory.condition_status || ""}
              onChange={(e) => handleMedicalHistoryChange("condition_status", e.target.value)}
              className="form-control rounded-pill border-0"
            >
              <option value="">Select status</option>
              {condition_statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.id}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div className="mb-3">
            <label className="mb-1">Notes</label>
            <textarea
              rows={2}
              value={newMedicalHistory.notes}
              onChange={(e) => handleMedicalHistoryChange("notes", e.target.value)}
              className="form-control rounded-3 border-0"
            />
          </div>

          <div className="d-flex justify-content-center gap-3">
            <button
              className="btn btn-sm"
              onClick={saveMedicalHistory}
              style={{
                background: purple,
                color: "#fff",
                borderRadius: "20px",
                padding: "4px 18px",
              }}
            >
              Save
            </button>

            {!isCreate && editingMedicalIndex !== null && (
              <button
                className="btn btn-sm"
                onClick={deleteCurrentMedicalHistory}
                style={{
                  background: "#c0392b",
                  color: "#fff",
                  borderRadius: "20px",
                  padding: "4px 18px",
                }}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      )}

      {/* LIST */}
      {isOpen &&
        medicalHistory.map((item, idx) => (
          <div
            key={item.id ?? idx}
            className="d-flex justify-content-between align-items-center mb-2"
            style={{ fontSize: "14px", gap: "8px" }}
          >
            <div>
              <div>
                {item.condition_name} | {item.condition_status}
              </div>
              <div style={{ fontSize: "12px", color: "#777" }}>{item.notes || "Medical details & status"}</div>
            </div>

            {!isCreate && (
              <FontAwesomeIcon
                icon={faPen}
                style={{ cursor: "pointer", color: purple, fontSize: "14px" }}
                onClick={() => {
                  setNewMedicalHistory({
                    id: item.id,
                    condition_id: item.condition_id ?? null,
                    condition_name: item.condition_name ?? "",
                    condition_status: item.condition_status ?? "",
                    notes: item.notes || "",
                  });

                  setEditingMedicalIndex(idx);
                  setShowAddMedicalHistory(true);

                  // custom if no condition_id but has name
                  setIsCustomSelected(item.condition_id == null && !!item.condition_name);
                }}
              />
            )}
          </div>
        ))}
    </div>
  );
};

export default MedicalHistorySubsection;